const {
  normalizeRows,
  toDraftPots
} = require('./budgetPlanningModel');
const {
  familyKey,
  json
} = require('../../lib/budgetFiscal');
const {
  budgetOverview
} = require('../../lib/budgetOverview');
const text = value => String(value ?? '').trim();
const metadataOf = row => json(row?.metadata);
const typeOf = row => text(row.nodeType || metadataOf(row).nodeType || 'budget').toLowerCase().replace(/[_\s]+/g, ' ');
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const regionList = value => [...new Set((Array.isArray(value) ? value : []).map(region => text(region?.code ?? region).toUpperCase()).filter(Boolean))].sort();
const sourceOf = row => text(row.fundingSource).toUpperCase();
const isGroup = row => row.planningGroup === true || metadataOf(row).planningGroup === true || typeOf(row) === 'group';
const isFundingPot = row => typeOf(row) === 'funding stream';
const potUse = row => isGroup(row) ? 'group' : isFundingPot(row) ? 'funding' : 'expense';

// Use the overview's existing public selections, so Planning follows its real
// classification without changing the behavior of any existing budget screen.
function categoryOf(row) {
  const fiscal = metadataOf(row).fiscalBudget || {};
  const pot = {
    id: String(row.id || 'planning'),
    parentId: null,
    potType: typeOf(row),
    fundingSource: row.fundingSource,
    code: row.code,
    name: row.name,
    displayCode: fiscal.displayCode,
    displayName: fiscal.displayName
  };
  for (const [selection, category] of [['group:crf', 'CRF'], ['group:ei', 'EI'], ['group:salary', 'salary'], ['group:other-admin', 'other-admin']]) {
    if (budgetOverview([pot], '2000-2001', selection).hasPots) return category;
  }
  return null;
}
function effectiveRegions(rows, id) {
  const byId = new Map(rows.map(row => [String(row.id), row]));
  const seen = new Set();
  let row = byId.get(String(id));
  while (row && !seen.has(String(row.id))) {
    seen.add(String(row.id));
    const codes = regionList(row.regions);
    if (codes.length) return codes;
    row = byId.get(String(row.parentId));
  }
  return [];
}
function publishedPot(row, catalog = []) {
  const exact = catalog.find(pot => String(pot.id) === String(row.id));
  if (exact) return exact;
  const key = metadataOf(row).fiscalBudget?.familyKey;
  if (!key) return null;
  return catalog.find(pot => familyKey(pot) === key) || null;
}
const codeReferences = row => [row.code, metadataOf(row).fiscalBudget?.displayCode];
function codeError(rows, rowId, code, catalog = []) {
  const value = text(code),
    fold = value.toLocaleLowerCase('en-CA');
  if (!value || value.length > 64) return 'Enter a budget code of 1–64 characters.';
  const row = rows.find(item => String(item.id) === String(rowId));
  const original = row && publishedPot(row, catalog);
  if (original && value !== text(original.code)) return 'Keep the existing code so records linked to this pot remain valid.';
  if (!original && /^\d+$/.test(value)) return 'Include a non-numeric character in the code so it cannot be mistaken for a pot ID.';
  const matchesCode = item => codeReferences(item).some(reference => text(reference).toLocaleLowerCase('en-CA') === fold);
  if (rows.some(item => String(item.id) !== String(rowId) && matchesCode(item))) return 'This code is already used by another pot in the draft.';
  if (!original && catalog.some(matchesCode)) {
    return 'This code already identifies an existing pot. Copy that budget to reuse the pot, or choose a different code.';
  }
  return undefined;
}

// Suggestions are conveniences for new pots, never replacements for copied identities.
function suggestPotCode(name, parent, rows = [], catalog = []) {
  const slug = value => text(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const parentReference = metadataOf(parent).fiscalBudget?.displayCode || parent?.code;
  const prefix = parent ? slug(parentReference && !/^GROUP-/i.test(parentReference) ? parentReference : metadataOf(parent).fiscalBudget?.displayName || parent.name).slice(0, 24).replace(/-+$/, '') : '';
  const initials = slug(name).split('-').filter(Boolean).map(word => /^\d+$/.test(word) ? word : word[0]).join('').slice(0, 12);
  let base = [prefix, initials || 'POT'].filter(Boolean).join('-');
  if (/^\d+$/.test(base)) base = `POT-${base}`;
  base = base.slice(0, 64).replace(/-+$/, '');
  const used = new Set([...rows, ...catalog].flatMap(codeReferences).map(value => text(value).toUpperCase()));
  let candidate = base;
  for (let suffix = 2; used.has(candidate); suffix += 1) {
    const ending = `-${suffix}`;
    candidate = `${base.slice(0, 64 - ending.length).replace(/-+$/, '')}${ending}`;
  }
  return candidate;
}
function detailsFor(row) {
  const metadata = metadataOf(row);
  return {
    name: metadata.fiscalBudget?.displayName || row.name || '',
    code: row.code || '',
    use: potUse(row),
    fundingSource: sourceOf(row),
    regions: regionList(row.regions),
    glProjectCodeExternal: row.glProjectCodeExternal || '',
    glProjectCodeInternal: row.glProjectCodeInternal || '',
    agreementId: row.agreementId || '',
    agreementCode: row.agreementCode || '',
    isRestricted: !!row.isRestricted,
    isAdminCap: !!row.isAdminCap,
    description: row.description ?? metadata.description ?? '',
    policyNotes: row.policyNotes ?? metadata.policyNotes ?? ''
  };
}
function updateDetails(rows, rowId, values, catalog = []) {
  const normalized = normalizeRows(rows);
  const row = normalized.find(item => item.id === String(rowId));
  if (!row) throw new Error('Choose a budget pot.');
  const original = publishedPot(row, catalog);
  const current = detailsFor(row);
  const requested = Object.assign({}, current, values);
  const name = text(requested.name);
  if (!name || name.length > 255) throw new Error('Enter a name of 1–255 characters.');
  const problem = codeError(normalized, rowId, requested.code, catalog);
  if (problem) throw new Error(problem);
  const use = requested.use;
  if (!['group', 'funding', 'expense'].includes(use)) throw new Error('Choose how this pot will be used.');
  if (original && use !== current.use) throw new Error('Create a separate pot to change how an existing pot is used.');
  if (use !== current.use && (use === 'group' || current.use === 'group')) {
    const hasChildren = normalized.some(item => item.parentId === row.id);
    const hasAmount = Number(row.approved) !== 0 || Number(row.adjusted) !== 0;
    if (current.use === 'group' && (hasChildren || hasAmount)) throw new Error('Only an empty group with zero allocations can become a money-holding pot.');
    if (use === 'group' && hasAmount) throw new Error('Use Add child to create a group while preserving this pot and its allocation.');
  }
  const fundingSource = text(requested.fundingSource).toUpperCase();
  if (fundingSource !== sourceOf(row) && fundingSource && !['EI', 'CRF', 'OTHER'].includes(fundingSource)) throw new Error('Choose a valid funding source.');
  if (original && fundingSource !== sourceOf(original)) throw new Error('Create a separate pot to change its funding source.');
  const regions = regionList(requested.regions);
  if (original && !same(regions, regionList(row.regions))) throw new Error('Keep the existing region assignment. Create a separate pot for a different region.');
  for (const key of ['glProjectCodeExternal', 'glProjectCodeInternal']) if (text(requested[key]).length > 64) throw new Error('Keep GL/project codes to 64 characters or fewer.');

  // Only write fields the user changed. A simple rename must not replace copied
  // nulls, legacy values, metadata-only settings, or settings unknown to this UI.
  const next = Object.assign({}, row, {
    metadata: Object.assign({}, row.metadata)
  });
  if (name !== current.name) {
    next.name = name;
    next.metadata.fiscalBudget = Object.assign({}, row.metadata.fiscalBudget, {
      displayName: name
    });
  }
  const code = text(requested.code);
  if (code !== row.code) {
    next.code = code;
    next.metadata.fiscalBudget = Object.assign({}, next.metadata.fiscalBudget, {
      displayCode: code
    });
  }
  if (use !== current.use) {
    next.nodeType = use === 'funding' ? 'Funding stream' : use === 'group' ? 'group' : 'budget';
    next.planningGroup = use === 'group';
    next.metadata = Object.assign({}, next.metadata, {
      nodeType: next.nodeType,
      planningGroup: next.planningGroup
    });
  }
  if (fundingSource !== sourceOf(row)) next.fundingSource = fundingSource || null;
  if (!same(regions, current.regions)) next.regions = regions;
  for (const key of ['glProjectCodeExternal', 'glProjectCodeInternal', 'agreementId', 'agreementCode']) {
    if (text(requested[key]) !== text(current[key])) next[key] = text(requested[key]) || null;
  }
  for (const key of ['isRestricted', 'isAdminCap']) if (!!requested[key] !== current[key]) next[key] = !!requested[key];
  for (const key of ['description', 'policyNotes']) {
    if (requested[key] !== current[key]) {
      next[key] = requested[key];
      next.metadata[key] = requested[key];
    }
  }
  if (original && !row.planningGroup && categoryOf(next) !== categoryOf(row)) {
    throw new Error('This name would change the pot’s salary or administration grouping. Keep that meaning in its name.');
  }
  return normalizeRows(normalized.map(item => item.id === row.id ? next : item));
}
function assertCopyTotals(pots) {
  const normalized = normalizeRows(pots);
  const calculated = new Map(toDraftPots(normalized).map(row => [row.id, row]));
  for (const row of normalized.filter(item => item.planningGroup)) {
    for (const field of ['approved', 'adjusted']) {
      const actual = Number(row[field]);
      if (!Number.isFinite(actual) || Math.abs(actual * 100 - calculated.get(row.id)[field] * 100) > 0.0001) {
        throw new Error(`${row.name}: its published ${field === 'approved' ? 'original' : 'current'} allocation does not equal its child allocations. Resolve the difference before copying so no allocation is lost.`);
      }
    }
  }
}
function reviewCompatibility(rows, catalog = [], availableRegions = [], {
  metadataReady = true
} = {}) {
  if (!metadataReady || !Array.isArray(catalog) || !Array.isArray(availableRegions)) {
    return [{
      id: null,
      name: 'Budget setup',
      message: 'Budget and region settings must finish loading before compatibility can be reviewed.'
    }];
  }
  const normalized = normalizeRows(rows);
  const messages = [];
  const validRegions = new Set(regionList(availableRegions));
  for (const row of normalized) {
    const issue = message => messages.push({
      id: row.id,
      name: row.name,
      message
    });
    const original = publishedPot(row, catalog);
    const code = codeError(normalized, row.id, row.code, catalog);
    if (code) issue(code);
    const codes = effectiveRegions(normalized, row.id);
    if (codes.some(region => !validRegions.has(region))) issue('Choose a region from the available list.');
    if (sourceOf(row) && !['EI', 'CRF', 'OTHER'].includes(sourceOf(row))) issue('Choose a valid funding source.');
    if (isFundingPot(row) && !row.planningGroup) {
      if (!sourceOf(row)) issue('Choose its funding source.');
      if (codes.length !== 1) issue('Choose one reporting region for this funding pot.');
      if (!regionList(row.regions).length) issue('Assign its region directly to this funding pot so it is available in funding selectors.');
    }
    if (original) {
      if (typeOf(row) !== typeOf(original)) issue('Keep the existing pot type.');
      if (sourceOf(row) !== sourceOf(original)) issue('Keep the existing funding source.');
      if (!same(regionList(row.regions), regionList(original.regions))) issue('Keep the existing region assignment.');
      if (!same(codes, effectiveRegions(catalog, original.id))) issue('Keep the existing reporting region.');
      if (!row.planningGroup && categoryOf(row) !== categoryOf(original)) issue('Keep its existing salary or administration grouping.');
    }
  }
  return messages;
}
module.exports = {
  isFundingPot,
  potUse,
  categoryOf,
  effectiveRegions,
  publishedPot,
  codeError,
  suggestPotCode,
  detailsFor,
  updateDetails,
  assertCopyTotals,
  reviewCompatibility
};
